import type { VoicedChord } from '@tunasong/music-lib'
import { useRef } from 'react'
import { useStabScheduler } from '../player/stab-scheduler.js'
import {
  buildVoiceLeadingStabs,
  type VoiceLeadingSequenceMode,
} from '../player/voice-leading-sequencer.js'

export interface UseVoiceLeadingAuditionProps {
  voicingA: VoicedChord | null
  voicingB: VoicedChord | null
  bpm: number
}

export interface VoiceLeadingAudition {
  /** Audition the pair (default) or a single voicing on demand. */
  play: (mode?: VoiceLeadingSequenceMode) => void
  stop: () => void
}

/**
 * Voice-Leading Lab audition hook. Thin wrapper over `useStabScheduler`.
 * `useStabScheduler.play()` takes no args, so the requested sequence mode is
 * staged in a ref that `buildStabs` reads on each iteration. A null voicing
 * yields an empty stab list (no-op), letting the host mount the hook
 * unconditionally regardless of phase.
 *
 * Spec: docs/superpowers/specs/2026-05-27-voice-leading-lab-v1-design.md §5
 */
export const useVoiceLeadingAudition = (props: UseVoiceLeadingAuditionProps): VoiceLeadingAudition => {
  const modeRef = useRef<VoiceLeadingSequenceMode>('pair')
  const { play: rawPlay, stop } = useStabScheduler({
    buildStabs: () =>
      props.voicingA && props.voicingB
        ? buildVoiceLeadingStabs({
            voicingA: props.voicingA,
            voicingB: props.voicingB,
            bpm: props.bpm,
            sequenceMode: modeRef.current,
          }).stabs
        : [],
  })
  const play = (mode: VoiceLeadingSequenceMode = 'pair'): void => {
    modeRef.current = mode
    rawPlay()
  }
  return { play, stop }
}
